import { type Actor } from '@shared/board'
import { type BoardSummary } from '@shared/api'
import { readCard } from '@shared/card-markdown'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BookOpen,
  ChevronLeft,
  LayoutDashboard,
  Maximize2,
  MessagesSquare,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { clsx } from 'clsx'
import { Link, useParams } from 'react-router'
import { AppHeader } from '@/components/AppHeader'
import { IconButton } from '@/components/Button'
import { BoardCanvas } from '@/board/BoardCanvas'
import { CanvasRoot } from '@/board/engine/xyflow/CanvasRoot'
import { BoardClient } from '@/board/client'
import {
  ActionSlot,
  BoardContext,
  type BoardCtx,
  BoardOverlay,
  CanvasControl,
  EngineSlot,
  ReaderControl,
  useBoardCtx,
} from '@/board/context'
import { Reader } from '@/materials/Reader'
import { SearchPalette } from '@/board/SearchPalette'
import { useMaterial } from '@/materials/hooks'
import {
  type Layout,
  readLayout,
  readSideCanvas,
  writeLayout,
  writeSideCanvas,
} from '@/board/layout'
import { useMediaQuery } from '@/lib/media'
import { readPref, writePref } from '@/lib/storage'
import { ChatPanel, Sidebar } from '@/session/Sidebar'
import { SessionsClient } from '@/session/store'
import { useI18n } from '@/i18n/i18n'
import { appTitle } from '@/i18n/messages'
import { api } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import '@/board/board.css'

export type Clients = { board: BoardClient; sessions: SessionsClient }

function useClients(boardId: string, enabled: boolean): Clients | null {
  const [clients, setClients] = useState<Clients | null>(null)
  useEffect(() => {
    if (!enabled) return
    const board = new BoardClient(boardId)
    const sessions = new SessionsClient(board, boardId)
    if (import.meta.env.DEV) {
      // Dev-only handles for e2e tests and evals (evals/): the board, its AI
      // sessions, and a card's text as markdown.
      Object.assign(window, {
        __moot: board,
        __mootSessions: sessions,
        __mootCard: (id: string) => readCard(board.doc, id),
      })
    }
    // The clients own a socket: they have to be created and torn down by an effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setClients({ board, sessions })
    return () => {
      sessions.destroy()
      board.destroy()
      setClients(null)
    }
  }, [boardId, enabled])
  return clients
}

function Status({ client }: { client: BoardClient }) {
  const { t } = useI18n()
  const { status } = useSyncExternalStore(client.subscribe, client.getSnapshot)
  if (status === 'connected') return null
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted" role="status">
      <span className="size-1.5 animate-pulse rounded-full bg-amber-500" />
      {t(`board.${status}`)}
    </span>
  )
}

function Canvas() {
  return (
    <CanvasRoot>
      <BoardCanvas />
    </CanvasRoot>
  )
}

/** Canvas-main: the whiteboard, with the conversation in a sidebar. */
function CanvasMain({ boardId, onChatMain }: { boardId: string; onChatMain: () => void }) {
  const { t } = useI18n()
  const { sessions } = useBoardCtx()
  const [sidebarOpen, setSidebarOpen] = useState(() => readPref(`sidebar:${boardId}`) !== 'closed')
  const toggleSidebar = (open: boolean) => {
    writePref(`sidebar:${boardId}`, open ? null : 'closed')
    setSidebarOpen(open)
  }
  // Something on the board asked to show a conversation: open the sidebar for
  // it. Only a new request opens it; closing it afterwards is the person's call.
  // (In chat-main the conversation is always on screen: nothing to open.)
  useEffect(() => {
    let seen = sessions.getSnapshot().reveal?.n ?? 0
    const off = sessions.subscribe(() => {
      const r = sessions.getSnapshot().reveal
      if (!r || r.n === seen) return
      seen = r.n
      if (r.open) {
        writePref(`sidebar:${boardId}`, null)
        setSidebarOpen(true)
      }
    })
    return () => {
      off()
    }
  }, [sessions, boardId])
  // Something wants to be said in the chat (the reader's "ask"): show the chat.
  useEffect(() => {
    let seen = sessions.getSnapshot().draft?.n ?? 0
    const off = sessions.subscribe(() => {
      const d = sessions.getSnapshot().draft
      if (!d || d.n === seen) return
      seen = d.n
      writePref(`sidebar:${boardId}`, null)
      setSidebarOpen(true)
    })
    return () => {
      off()
    }
  }, [sessions, boardId])
  const { reader } = useBoardCtx()
  const readerOpen = useSyncExternalStore(reader.subscribe, reader.getSnapshot) !== null
  return (
    <div className="relative flex h-full">
      {/* The reader sits beside the canvas, which gives it room: nothing on the board is hidden under it. */}
      {readerOpen && (
        <section
          aria-label={t('material.open')}
          className="relative h-full w-[min(36%,640px)] min-w-[320px] flex-none animate-[side-in_240ms_var(--ease-spring)] border-r border-line max-[899px]:absolute max-[899px]:inset-0 max-[899px]:z-20 max-[899px]:w-full max-[899px]:min-w-0"
        >
          <Reader
            onClose={() => {
              reader.close()
            }}
          />
        </section>
      )}
      <div className="relative min-w-0 flex-1">
        <Canvas />
        {!sidebarOpen && (
          <button
            type="button"
            onClick={() => {
              toggleSidebar(true)
            }}
            className="absolute top-3 right-3 z-10 inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-(--chrome) px-3 text-[13px] font-medium text-ink-2 shadow-md backdrop-blur-xl hover:text-ink"
          >
            <MessagesSquare size={16} />
            {t('sidebar.open')}
          </button>
        )}
      </div>
      {sidebarOpen && (
        <Sidebar
          onCollapse={() => {
            toggleSidebar(false)
          }}
          onChatMain={onChatMain}
        />
      )}
    </div>
  )
}

/**
 * Chat-main: the conversation is the page; the whiteboard is a side canvas
 * that slides in when something asks for it (an embed, a mention) or when
 * toggled, and can take over as canvas-main from its corner.
 */
function ChatMain({
  sideOpen,
  onSide,
  sideView,
  onSideView,
  onCanvasMain,
  headerSlot,
}: {
  sideOpen: boolean
  onSide: (open: boolean) => void
  /** What the side shows: the board, or the reader (MATERIALS.md). */
  sideView: SideView
  onSideView: (v: SideView) => void
  onCanvasMain: () => void
  /** The app header's slot for the session controls, when there is room for them there. */
  headerSlot: HTMLElement | null
}) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const target = useSyncExternalStore(reader.subscribe, reader.getSnapshot)
  const material = useMaterial(target?.materialId ?? '')
  const reading = sideView === 'reader' && target !== null
  return (
    <div className="relative flex h-full">
      <ChatPanel
        variant="main"
        headerSlot={headerSlot}
        trailing={
          <IconButton
            label={sideOpen ? t('canvas.hide') : t('canvas.show')}
            aria-pressed={sideOpen}
            onClick={() => {
              onSide(!sideOpen)
            }}
          >
            {sideOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </IconButton>
        }
      />
      {sideOpen && (
        <section
          aria-label={t('canvas.label')}
          className={
            'relative h-full w-[min(58%,960px)] min-w-[360px] flex-none animate-[side-in_240ms_var(--ease-spring)] border-l border-line bg-canvas ' +
            // Narrow screens: no room beside the chat, so it covers it.
            'max-[899px]:absolute max-[899px]:inset-0 max-[899px]:z-20 max-[899px]:w-full max-[899px]:min-w-0 max-[899px]:border-l-0'
          }
        >
          <Canvas />
          {target && !reading && (
            <button
              type="button"
              onClick={() => {
                onSideView('reader')
              }}
              className="absolute top-3 left-3 z-10 inline-flex h-9 max-w-[50%] items-center gap-2 rounded-lg border border-line bg-(--chrome) px-3 text-[13px] font-medium text-ink-2 shadow-md backdrop-blur-xl hover:text-ink"
            >
              <BookOpen size={15} className="flex-none" />
              <span className="truncate">{material?.name ?? t('material.missing')}</span>
            </button>
          )}
          {reading && (
            <div className="absolute inset-0 z-20 flex flex-col">
              <div className="flex h-9 flex-none items-center border-b border-line bg-surface px-2">
                <button
                  type="button"
                  onClick={() => {
                    onSideView('board')
                  }}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium text-muted hover:bg-surface-2 hover:text-ink"
                >
                  <LayoutDashboard size={14} />
                  {t('reader.tabBoard')}
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <Reader
                  onClose={() => {
                    reader.close()
                    onSideView('board')
                  }}
                />
              </div>
            </div>
          )}
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg border border-line bg-(--chrome) p-0.5 shadow-md backdrop-blur-xl">
            <IconButton label={t('layout.toCanvas')} onClick={onCanvasMain}>
              <Maximize2 size={15} />
            </IconButton>
            <IconButton
              label={t('canvas.hide')}
              onClick={() => {
                onSide(false)
              }}
            >
              <PanelRightClose size={15} />
            </IconButton>
          </div>
        </section>
      )}
    </div>
  )
}

type SideView = 'board' | 'reader'

/** The two layouts, named: which one is the main view is a choice worth seeing. */
function LayoutSwitch({ layout, onLayout }: { layout: Layout; onLayout: (l: Layout) => void }) {
  const { t } = useI18n()
  const options: { value: Layout; label: string; icon: ReactNode }[] = [
    { value: 'chat', label: t('layout.chat'), icon: <MessagesSquare size={14} /> },
    { value: 'canvas', label: t('layout.canvas'), icon: <LayoutDashboard size={14} /> },
  ]
  return (
    <div
      role="radiogroup"
      aria-label={t('layout.label')}
      className="ml-2 inline-flex flex-none items-center rounded-lg border border-line bg-surface-2 p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={layout === o.value}
          title={o.value === 'chat' ? t('layout.toChat') : t('layout.toCanvas')}
          onClick={() => {
            if (layout !== o.value) onLayout(o.value)
          }}
          className={clsx(
            'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium transition-colors',
            layout === o.value ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
          )}
        >
          {o.icon}
          <span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
  )
}

/** When the namer titles this board while it is open, show the new title. */
function useNamedTitle(boardId: string, sessions: SessionsClient | null) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!sessions) return
    let last = sessions.getSnapshot().boardTitle
    const off = sessions.subscribe(() => {
      const title = sessions.getSnapshot().boardTitle
      if (!title || title === last) return
      last = title
      queryClient.setQueryData<BoardSummary>(['board', boardId], (old) =>
        old ? { ...old, title } : old,
      )
      void queryClient.invalidateQueries({ queryKey: ['boards'] })
    })
    return () => {
      off()
    }
  }, [boardId, sessions, queryClient])
}

/**
 * A board on screen: the header, both layouts, the chat and the canvas, for
 * `clients`. The board page gives it live clients; a gallery preview gives it
 * offline, read-only ones (`readOnly`), plus its own header actions.
 */
export function BoardWorkspace({
  id,
  title,
  clients,
  readOnly = false,
  failed = false,
  headerExtra,
  notice,
}: {
  id: string
  title: string | undefined
  clients: Clients | null
  readOnly?: boolean
  failed?: boolean
  headerExtra?: ReactNode
  /** A quiet line under the header (e.g. a gallery example's medical note). */
  notice?: ReactNode
}) {
  const { t } = useI18n()
  const session = authClient.useSession()
  const client = clients?.board ?? null
  useNamedTitle(id, readOnly ? null : (clients?.sessions ?? null))
  const [engine] = useState(() => new EngineSlot())
  const [actions] = useState(() => new ActionSlot())
  const [layout, setLayoutState] = useState<Layout>(() => readLayout(id))
  const [sideOpen, setSideState] = useState(() => readSideCanvas(id))
  const setLayout = (l: Layout) => {
    writeLayout(id, l)
    setLayoutState(l)
  }
  const setSide = (open: boolean) => {
    writeSideCanvas(id, open)
    setSideState(open)
  }
  // What the chat calls to show the board: in chat-main it slides the side
  // canvas in; in canvas-main the canvas is already there.
  const [canvas] = useState(() => new CanvasControl(engine))
  const [overlay] = useState(() => new BoardOverlay())
  const [reader] = useState(() => new ReaderControl())
  const [sideView, setSideView] = useState<SideView>('board')
  useLayoutEffect(() => {
    canvas.onReveal(() => {
      setSideView('board')
      if (layout !== 'chat' || sideOpen) return
      writeSideCanvas(id, true)
      setSideState(true)
    })
  }, [canvas, layout, sideOpen, id])
  // Opening a material shows the reader; in chat-main, in the side.
  useEffect(() => {
    let seen = reader.getSnapshot()?.n ?? 0
    const off = reader.subscribe(() => {
      const r = reader.getSnapshot()
      if (!r || r.n === seen) return
      seen = r.n
      setSideView('reader')
      if (layout === 'chat' && !sideOpen) {
        writeSideCanvas(id, true)
        setSideState(true)
      }
    })
    return () => {
      off()
    }
  }, [reader, layout, sideOpen, id])
  const userId = session.data?.user.id
  const wide = useMediaQuery('(min-width: 768px)')
  const foldHeader = layout === 'chat' && wide
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null)

  const ctx = useMemo<BoardCtx | null>(() => {
    if (!clients || !userId) return null
    const me: Actor = { kind: 'user', id: userId }
    return {
      boardId: id,
      client: clients.board,
      sessions: clients.sessions,
      engine,
      canvas,
      overlay,
      reader,
      actions,
      me,
      autofocus: new Set(),
      initialText: new Map(),
    }
  }, [clients, userId, id, engine, canvas, overlay, reader, actions])

  return (
    <div className="flex h-full flex-col">
      <AppHeader>
        <Link
          to="/"
          aria-label={t('board.back')}
          className="inline-flex flex-none items-center gap-1 rounded-md px-1.5 py-1 text-[13px] whitespace-nowrap text-muted hover:bg-surface-2 hover:text-ink"
        >
          <ChevronLeft size={15} />
          <span className="hidden sm:inline">{t('board.back')}</span>
        </Link>
        <span className="text-line-strong">/</span>
        <span className="truncate text-[14px] font-medium">{title}</span>
        <LayoutSwitch layout={layout} onLayout={setLayout} />
        <span className="flex-1" />
        {client && !readOnly && <Status client={client} />}
        {/* Chat-main on a wide screen: one row of chrome, the session controls up here. */}
        {foldHeader && <div ref={setHeaderSlot} className="flex min-w-0 items-center" />}
        {headerExtra}
      </AppHeader>
      {notice && (
        <div className="flex-none border-b border-line bg-surface px-4 py-1.5">{notice}</div>
      )}
      <main className="relative flex-1 overflow-hidden bg-canvas">
        {failed && (
          <div className="grid h-full place-items-center px-4 text-center text-sm text-muted">
            {t('board.notFound')}
          </div>
        )}
        {ctx && (
          <BoardContext value={ctx}>
            <SearchPalette />
            {layout === 'canvas' ? (
              <CanvasMain
                boardId={id}
                onChatMain={() => {
                  setLayout('chat')
                }}
              />
            ) : (
              <ChatMain
                headerSlot={foldHeader ? headerSlot : null}
                sideOpen={sideOpen}
                onSide={setSide}
                sideView={sideView}
                onSideView={setSideView}
                onCanvasMain={() => {
                  setLayout('canvas')
                }}
              />
            )}
          </BoardContext>
        )}
      </main>
    </div>
  )
}

export function Board() {
  const { id = '' } = useParams()
  const board = useQuery({
    queryKey: ['board', id],
    queryFn: () => api.boards.get(id),
    retry: false,
  })
  const clients = useClients(id, board.isSuccess)

  const { locale } = useI18n()
  useEffect(() => {
    if (board.data) document.title = `${board.data.title} · moot`
    return () => {
      document.title = appTitle(locale)
    }
  }, [board.data, locale])

  return (
    <BoardWorkspace id={id} title={board.data?.title} clients={clients} failed={board.isError} />
  )
}
